import {put, call, all} from 'redux-saga/effects';
import Transaction from "../../entities/transaction";
import * as Actions from "../actions/transactionActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type TransactionAction = {
    transactionId: string;
    transaction: Transaction;
    transactions: Transaction[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneTransaction(action: TransactionAction) {
	const session = tabSession.captureGeneration();
	try {
		const transaction: Transaction = yield call(api.findOne, 'transaction', action.transactionId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.TRANSACTION_FETCH_ONE_SUCCESS, transactionId: action.transactionId, transaction, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TRANSACTION_FETCH_ONE_FAILURE, transactionId: action.transactionId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {TransactionAction} action
 */
export function* saveTransaction(action: TransactionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ TRANSACTION_SAVE saga', action.transaction?.id);

		if (action.transaction === undefined || action.transaction === null) {
			console.error('saveTransaction received a null');
			throw new Error('saveTransaction received a null');
		}

		if (!(action.transaction instanceof Transaction)) {
			action.transaction = new Transaction(action.transaction);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.transaction.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.transaction);
			const addResult = yield call(idb.add, 'transaction', action.transaction);
			if (addResult.isErr()) {
				console.error(`⮂ saveTransaction: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertTransactionSave(action.transaction, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.TRANSACTION_SAVE_FAILURE, transaction: action.transaction, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.TRANSACTION_SAVE, transaction: action.transaction, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertTransactionSave(action.transaction, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.TRANSACTION_SAVE_FAILURE, transaction: action.transaction, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {TransactionAction} action
 * @param {Transaction[]} action.transactions
 */
export function* saveManyTransactions(action: TransactionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ TRANSACTION_SAVE_MANY saga', action.transactions.map(transaction => transaction.id).join(','));

        if (!Array.isArray(action.transactions)) {
            console.error('saveManyTransactions expected an array, received', action.transactions);
            throw new Error('saveManyTransactions expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveTransaction), under these queue keys
		const dispatchedAddedOns: number[] = action.transactions.map(transaction => transaction?.addedOn);
		const revertAll = () => action.transactions.map((transaction, i) =>
			put(Actions.revertTransactionSave(transaction, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Transaction[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.transactions.length; i++) {
				const transaction = action.transactions[i];
				transaction.persisted = false;
				ensureOperationId(transaction);
				toSave.push(transaction);
			}

			const addResult = yield call(idb.add, 'transaction', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyTransactions: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.TRANSACTION_SAVE_MANY_FAILURE, transactions: action.transactions, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.TRANSACTION_SAVE_MANY, transactions: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.TRANSACTION_SAVE_MANY_FAILURE, transactions: action.transactions, status: 500, err});

			console.error(`Error saving ${action.transactions.length} transactions to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {TransactionAction} action
 */
export function* removeTransaction(action: TransactionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.transaction === undefined || action.transaction === null || !(action.transaction instanceof Transaction)) {
			console.error('removeTransaction expected type Transaction, received', action.transaction);
			reportQueued(false);
			throw new Error('removeTransaction expected type Transaction');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.transaction.status = 'D';
		action.transaction.addedOn = newHRTimestamp();
		action.transaction.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.transaction);
		const addResult = yield call(idb.add, 'transaction', action.transaction);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeTransaction: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.TRANSACTION_REMOVE_FAILURE, transaction: action.transaction, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TRANSACTION_REMOVE, transaction: action.transaction});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TRANSACTION_REMOVE_FAILURE, transaction: action.transaction, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Transaction.
	*
	* @param {TransactionAction} action
	*/
export function* restoreTransaction(action: TransactionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreTransaction expected getCurrentUserId() to return a value');
        return;
    }

	if (action.transaction === undefined || action.transaction === null) {
        console.error('restoreTransaction expected a Transaction, received null');
        throw new Error('restoreTransaction expected a Transaction, received null');
    }
    else if (!(action.transaction instanceof Transaction)) action.transaction = new Transaction(action.transaction); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.transaction.status = 'U';
		action.transaction.addedOn = newHRTimestamp();
		action.transaction.addedBy = userId;
		action.transaction.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.transaction);
		const addResult = yield call(idb.add, 'transaction', action.transaction);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreTransaction: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TRANSACTION_RESTORE_FAILURE, transaction: action.transaction, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TRANSACTION_RESTORE, transaction: action.transaction});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TRANSACTION_RESTORE_FAILURE, transaction: action.transaction, status: 500, err});

		console.error('Error restoring from database', err);
	}
}